// UploadGuide - "what to upload" for the full technical documentation
// (owner, 2026-09-19, asked for Tesla; generic). A short list of the
// documents that give the whole report, each ticked when a matching
// document is filed - read from the same import checklist the glance keeps
// (GET /glance/inputs), so a tick here and a tick there always agree.
// Open by itself while a project has few documents; one line after that.
import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

// Each item: what to send, why, and which checklist slots tick it.
export const ITEMS = [
  { key: "arch", label: "Architectural floor plans, every level", why: "rooms, the building outline and zones - DWG is best", slots: ["arch-plans", "revit"] },
  { key: "rcp", label: "Reflected ceiling plans, every level", why: "light fixtures, sprinkler heads, ceiling heights", slots: ["rcp"] },
  { key: "grid", label: "At least one sheet per level that shows the building grid", why: "every other sheet is placed on the building by its grid", grid: true },
  { key: "mep", label: "MEP plans per level: power, lighting, mechanical, plumbing, fire protection", why: "panels, fixtures, ducts, terminal units, pipes", slots: ["power", "lighting", "mechanical", "plumbing", "fire"] },
  { key: "lv", label: "Telecom, security and low-voltage sets", why: "pathways, sleeves, telecom rooms, security conduit", slots: ["telecom", "security-das"] },
  { key: "specs", label: "Specifications (Divisions 22, 23, 26, 27, 28)", why: "the rules shown on the pages", slots: ["specs"] },
  { key: "schedules", label: "Equipment and panel schedules, or the energy model", why: "equipment capacities, what each unit serves, the checks against the plans", slots: ["mech-schedules", "panel-schedules", "energy"] },
  { key: "program", label: "Construction schedule, look-aheads and field trackers", why: "progress by zone and the look-ahead", slots: ["construction-schedule", "tracking"] },
];

// An item is in when any of its slots is received or partial (or, for the
// grid, when the building data has one).
export function ticks(data) {
  const byId = Object.fromEntries(((data && data.slots) || []).map((s) => [s.id, s.status]));
  return ITEMS.map((it) => ({ ...it, done: it.grid ? !!(data && data.grid) : it.slots.some((id) => byId[id] === "received" || byId[id] === "partial") }));
}

export default function UploadGuide({ projectId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    fetch(`${url}/projects/${projectId}/glance/inputs`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null)).then((b) => { if (alive) setData(b); }).catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [projectId]);
  if (!data) return null;
  const list = ticks(data);
  const done = list.filter((i) => i.done).length;
  const few = (data.documents || 0) < 25 || done < 5;
  const shown = open == null ? few : open;
  if (!shown) {
    return (
      <p className="g-note g-noprint bl-upload-guide">
        What gives the full report: {done} of {list.length} in. <Button type="link" size="small" onClick={() => setOpen(true)}>See the list</Button>
      </p>
    );
  }
  return (
    <section className="bl-glance bl-upload-guide g-noprint" aria-label="What to upload">
      <h3>What to upload for the full report <span className="g-tsub">{done} of {list.length} in{data.documents ? ` · ${data.documents} documents filed` : ""}</span></h3>
      <ul className="g-ug-list">
        {list.map((it) => (
          <li key={it.key} className={it.done ? "is-done" : ""}>
            <span className="g-ug-mark" aria-label={it.done ? "Filed" : "Not yet"}>{it.done ? "✓" : "–"}</span>
            <b>{it.label}</b> <span className="g-tsub">{it.why}</span>
          </li>
        ))}
      </ul>
      <p className="g-note">
        <b>Name files so the level can be read:</b> put the level in the file or sheet name (&quot;Level 2&quot;, &quot;L2&quot;, &quot;SECOND FLOOR&quot;) and the area letter when a level is split
        (&quot;AREA &apos;A&apos;&quot;). A whole drawing set in one PDF is fine: it is split into sheets and each is read by its title block. DWG gives rooms and exact
        positions; a PDF plan works when it states its scale and shows the grid bubbles.
      </p>
      {!few && <Button size="small" onClick={() => setOpen(false)}>Hide</Button>}
    </section>
  );
}
